<template>
  <div class="container-fluid bb-player-combat-view px-0">
    <CombatNavbar
      playerMode
      :hideScoreboard="isArenaInactive"
    />

    <div class="container-fluid px-4 py-3 bb-player-combat-content">
      <div
        v-if="errorMessage"
        class="alert alert-danger mt-3"
      >
        {{ errorMessage }}
      </div>

      <div
        v-if="isLoading"
        class="bb-loader bb-player-loader"
      ></div>

      <div
        v-else-if="arena_locked"
        class="bb-player-locked-state"
      >
        <img
          :src="lockArenaImg"
          class="bb-player-lock-image"
          alt="Combat Locked"
        />
        <div class="bb-error-wrap bb-player-locked-message mt-4">
          Combat locked by DM at this time
        </div>
      </div>

      <div
        v-else-if="showWaitingForDm"
        class="bb-player-waiting-state"
      >
        <div
          class="bb-egg-timer"
          aria-hidden="true"
        >
          <div class="bb-egg-timer-sand bb-egg-timer-sand-top"></div>
          <div class="bb-egg-timer-sand bb-egg-timer-sand-bottom"></div>
        </div>
        <div class="bb-error-wrap bb-player-waiting-message mt-4">
          Waiting for DM
        </div>
      </div>

      <div
        v-else-if="loadReady"
        class="row bb-player-combat-row"
      >
        <div class="col-md-1 rounded">
          <CombatButtonsPanel
            :filterStatus="filterStatus"
            :arena_active_combatants="arena_active_combatants"
            :arena_inactive_combatants="arena_inactive_combatants"
            :arena_status="arena_status"
            :arena_round="arena_round"
            :arena_action="arena_action"
            :view_round="view_round"
            :arena_rolled="arena_rolled"
            :arena_extra_ready="arena_extra_ready"
            :arena_extra_action="arena_extra_action"
            :arena_bleeders_ready="arena_bleeders_ready"
            :arena_round_completed="arena_round_completed"
            :arena_in_progress="arena_in_progress"
            :outstanding_rolls="false"
            :arena_locked="arena_locked"
            :arena_ended="false"
            :race_count="0"
            :toggleDesc="toggleDesc"
            :toggleNumbers="false"
            :playerMode="true"
          />
        </div>
        <div class="col-xl-11 bb-player-table-column">
          <div class="bb-player-table-surface bb-previous-round-surface">
            <div
              v-if="combatTextOverlayVisible"
              :key="combatTextOverlayKey"
              class="bb-combat-text-overlay"
              aria-live="polite"
            >
              <div class="bb-combat-text-overlay__content">
                {{ combatTextOverlayMessage }}
              </div>
            </div>
            <TableCombatants
              :headings="headings"
              :combatants="combatants"
              :selected_action="null"
              :selected_reaction="null"
              :selected_marker="null"
              :selected_number="0"
              :arena_round="arena_round"
              :view_round="view_round"
              :arena_action="arena_action"
              :toggleDesc="toggleDesc"
              :toggleNumbers="false"
              :arena_status="arena_status"
              :arena_rolled="arena_rolled"
              :arena_in_progress="arena_in_progress"
              :arena_extra_ready="arena_extra_ready"
              :arena_extra_action="arena_extra_action"
              :arena_round_completed="arena_round_completed"
              :filterStatus="filterStatus"
              :minBleedCount="0"
              :maxBleedCount="0"
              :readOnly="true"
              :counterOnlyActionMarkers="true"
            />
          </div>
        </div>
      </div>
    </div>

    <PartySkillsView v-if="showPartySkillsViewer" />
  </div>
</template>

<script>
import { inject } from 'vue'
import { mapActions, mapGetters, mapMutations } from 'vuex'

import CombatButtonsPanel from '../components/CombatButtonsPanel.vue'
import CombatNavbar from '../components/CombatNavbar.vue'
import TableCombatants from '../components/TableCombatants.vue'
import PartySkillsView from './PartySkillsView.vue'

import createSocketConnection from '../helpers/createSocketConnection'
import changeViewRound from '../helpers/changeViewRound'
import getArenaSubStatus from '../helpers/getArenaSubStatus'
import getBleeders from '../helpers/getBleeders'
import getCombatantPreviousRoundActionImage from '../helpers/getCombatantPreviousRoundActionImage'
import getCombatantExtraAttackInvalidReason from '../helpers/getCombatantExtraAttackInvalidReason'
import getCombatantLastRoundActionImage from '../helpers/getCombatantLastRoundActionImage'
import getCombatantNextRoundAction from '../helpers/getCombatantNextRoundAction'
import getCombatantNextRoundMarker from '../helpers/getCombatantNextRoundMarker'
import getDescDetailCols from '../helpers/getDescDetailCols'
import getDescSummaryCols from '../helpers/getDescSummaryCols'
import getExtraAttacks from '../helpers/getExtraAttacks'
import getFilterStatusDesc from '../helpers/getFilterStatusDesc'
import getToggleExtraCols from '../helpers/getToggleExtraCols'
import loadCampaignRoundSlots from '../helpers/loadCampaignRoundSlots'
import loadCombatantExtraImages from '../helpers/loadCombatantExtraImages'
import loadCombatantHeadlineMarkers from '../helpers/loadCombatantHeadlineMarkers'
import loadCombatantImages from '../helpers/loadCombatantImages'
import loadSpecificCombatantExtraImage from '../helpers/loadSpecificCombatantExtraImage'
import loadLocalCombatantProps from '../helpers/loadLocalCombatantProps'
import refreshCombatantOrder from '../helpers/refreshCombatantOrder'
import setLocalCombatantsExtras from '../helpers/setLocalCombatantsExtras'
import lockArenaImg from '../assets/images/bbLockArena.png'

export default {
  name: 'PlayerCombatView',
  setup() {
    const global = inject('global')
    return { global }
  },
  components: {
    CombatButtonsPanel,
    CombatNavbar,
    PartySkillsView,
    TableCombatants,
  },
  props: {
    slug: {
      type: String,
      required: false,
      default: '',
    },
  },
  data() {
    return {
      lockArenaImg,
      isLoading: false,
      loadReady: false,
      errorMessage: '',
      arenaSocketConnection: null,
      headings: [],
      combatants: [],
      filterStatus: true,
      toggleDesc: true,
      arena_status: '',
      arena_round: 0,
      arena_action: 0,
      arena_locked: false,
      arena_rolled: false,
      arena_in_progress: false,
      arena_extra_ready: false,
      arena_extra_action: 0,
      arena_bleeders_ready: false,
      arena_round_completed: false,
      view_round: 0,
      maxCA: 0,
      maxCR: 0,
      new_col: 0,
      playerCombatPhase: 'standard',
      isReloadingArenaState: false,
      hasPendingArenaReload: false,
      combatTextOverlayVisible: false,
      combatTextOverlayMessage: '',
      combatTextOverlayKey: 0,
      combatTextOverlayTimer: null,
    }
  },
  computed: {
    ...mapGetters('combat', {
      getActiveCombatantCount: 'getActiveCombatantCount',
      getInactiveCombatantCount: 'getInactiveCombatantCount',
      getCombatants: 'getCombatants',
      getConfig: 'getConfig',
      getCampaign: 'getCampaign',
    }),
    arenaSlug() {
      return this.slug || this.$route.params.slug || ''
    },
    arena_active_combatants() {
      return this.getActiveCombatantCount
    },
    arena_inactive_combatants() {
      return this.getInactiveCombatantCount
    },
    arenaSubStatus() {
      return getArenaSubStatus({
        isInitialisingCombat: false,
        arena_status: this.arena_status,
        arena_ended: false,
        toggleNumbers: false,
        awaitingRollOffs: false,
        arena_rolled: this.arena_rolled,
        arena_extra_ready: this.arena_extra_ready,
        arena_extra_action: this.arena_extra_action,
        arena_bleeders_ready: this.arena_bleeders_ready,
        arena_round_completed: this.arena_round_completed,
      })
    },
    showWaitingForDm() {
      return !this.arena_locked && this.isArenaInactive
    },
    isArenaInactive() {
      return String(this.arena_status || '').toLowerCase() === 'inactive'
    },
    showPartySkillsViewer() {
      return this.$route.query.popup === 'party-skills'
    },
  },
  watch: {
    arenaSubStatus: {
      immediate: true,
      handler(nextStatus) {
        this.setArenaSubStatus(nextStatus)
      },
    },
    arenaSlug: {
      immediate: true,
      handler(newSlug) {
        if (!newSlug) {
          this.errorMessage = 'No active combat campaign is loaded.'
          this.loadReady = false
          return
        }

        void this.loadPlayerCombat(newSlug)
      },
    },
  },
  methods: {
    ...mapMutations('auth', {
      setCurrentView: 'setCurrentView',
    }),
    ...mapMutations('combat', {
      setViewRound: 'setViewRound',
      setArenaSubStatus: 'setArenaSubStatus',
    }),
    ...mapActions('combat', {
      loadConfig: 'loadConfig',
      loadCampaign: 'loadCampaign',
      loadStaticStatuses: 'loadStaticStatuses',
      loadStaticMarkers: 'loadStaticMarkers',
      loadStaticOptions: 'loadStaticOptions',
      loadStaticVisibilities: 'loadStaticVisibilities',
      loadStaticFatigues: 'loadStaticFatigues',
      loadStaticActions: 'loadStaticActions',
      loadStaticReactions: 'loadStaticReactions',
      loadStaticRoles: 'loadStaticRoles',
    }),
    SetView(view) {
      this.setCurrentView(view)
    },
    createBaseHeadings() {
      return [
        {
          id: 1,
          label: '',
          field: 'image',
          width: '60px',
          sortable: false,
          tdClass: 'text-center',
          hidden: false,
        },
        {
          id: 2,
          label: 'Seq',
          field: 'seq',
          width: '60px',
          type: 'number',
          tdClass: 'text-center',
          sortable: false,
          formatFn: this.global.hideZeroMinusValues,
          hidden: false,
        },
        {
          id: 3,
          label: 'Name',
          field: 'name',
          width: '240px',
          tdClass: 'text-left',
          sortable: false,
          hidden: false,
        },
        {
          id: 4,
          label: 'Player',
          field: 'player_name',
          tdClass: 'text-center',
          width: '80px',
          sortable: false,
        },
        {
          id: 5,
          label: 'Status',
          field: 'sub_status_image',
          sortable: false,
          tdClass: 'text-center bb-combat-image-cell',
          width: '100px',
          hidden: false,
        },
        {
          id: 6,
          label: 'Visibility',
          field: 'visibility_image',
          sortable: false,
          tdClass: 'text-center bb-combat-image-cell',
          width: '100px',
          hidden: false,
        },
        {
          id: 7,
          label: 'Fatigue',
          field: 'fatigue_image',
          sortable: false,
          tdClass: 'text-center bb-combat-image-cell',
          width: '100px',
          hidden: false,
        },
        {
          id: 8,
          label: 'Surprise',
          field: 'surprise_image',
          sortable: false,
          tdClass: 'text-center bb-combat-image-cell',
          width: '120px',
          hidden: false,
        },
        {
          id: 9,
          label: 'Bleed',
          field: 'bleeding_image',
          sortable: false,
          tdClass: 'text-center bb-combat-image-cell',
          width: '100px',
          hidden: false,
        },
        {
          id: 10,
          label: 'Options',
          field: 'options_image',
          sortable: false,
          tdClass: 'text-center bb-combat-image-cell',
          width: '168px',
          hidden: false,
        },
        {
          id: 11,
          label: 'Markers',
          field: 'markers_image',
          sortable: false,
          tdClass: 'text-center bb-combat-image-cell',
          width: '118px',
          hidden: false,
        },
        {
          id: 12,
          label: 'Category',
          field: 'category',
          sortable: false,
          tdClass: 'text-center',
          hidden: false,
        },
        {
          id: 13,
          label: 'Type',
          field: 'type',
          tdClass: 'text-center',
          hidden: false,
        },
        {
          id: 14,
          label: 'Race',
          field: 'race_name',
          tdClass: 'text-center',
          hidden: false,
        },
        {
          id: 15,
          label: 'Role',
          field: 'role',
          tdClass: 'text-center',
          hidden: false,
          sortable: false,
          formatFn: this.global.hideDefaultValues,
        },
        {
          id: 16,
          label: 'Status',
          field: 'status',
          tdClass: 'text-center',
          filterable: false,
          sortable: false,
          hidden: true,
        },
        {
          id: 17,
          label: 'Sub Status',
          field: 'sub_status',
          tdClass: 'text-center',
          filterable: false,
          sortable: false,
          hidden: true,
        },
        {
          id: 18,
          label: 'DEX',
          field: 'dex',
          type: 'number',
          tdClass: 'text-center',
          sortable: false,
          hidden: false,
        },
        {
          id: 19,
          label: 'CA',
          field: 'ca',
          type: 'number',
          tdClass: 'text-center',
          sortable: false,
          hidden: false,
        },
        {
          id: 20,
          label: 'CR',
          field: 'cr',
          type: 'number',
          tdClass: 'text-center',
          sortable: false,
          hidden: false,
        },
        {
          id: 21,
          label: 'Dam',
          field: 'damage_mod',
          tdClass: 'text-center',
          sortable: false,
          hidden: false,
          formatFn: this.global.hideZeroMinusValues,
        },
        {
          id: 22,
          label: '',
          field: 'fill4',
          filterable: false,
          sortable: false,
          tdClass: 'bb-is-fill-wide',
          thClass: 'bb-is-fill-wide',
          width: '1px',
          hidden: false,
        },
        {
          id: 29,
          label: 'Rank',
          field: 'rank',
          type: 'number',
          tdClass: 'text-center',
          formatFn: this.global.hideZeroMinusValues,
          sortable: false,
          hidden: false,
        },
        {
          id: 23,
          label: 'SR',
          field: 'sr',
          type: 'number',
          tdClass: 'text-center',
          sortable: false,
          formatFn: this.global.hideZeroMinusValues,
          hidden: false,
        },
        {
          id: 24,
          label: 'SR Adj',
          field: 'sr_adj',
          type: 'number',
          tdClass: 'text-center',
          formatFn: this.global.hideZeroMinusValues,
          sortable: false,
          hidden: false,
        },
        {
          id: 25,
          label: 'SR Roll',
          field: 'sr_roll',
          type: 'number',
          tdClass: 'text-center',
          sortable: false,
          formatFn: this.global.hideZeroMinusValues,
          hidden: false,
        },
        {
          id: 26,
          label: 'Roll Off',
          field: 'roll_off',
          type: 'number',
          tdClass: 'text-center',
          formatFn: this.global.hideZeroMinusValues,
          sortable: false,
          hidden: false,
        },
        {
          id: 34,
          label: '',
          field: 'fill1',
          filterable: false,
          sortable: false,
          tdClass: 'bb-is-fill-wide',
          thClass: 'bb-is-fill-wide',
          width: '1px',
          hidden: false,
        },
        {
          id: 27,
          label: 'SR Roll',
          field: 'sr_roll_image',
          tdClass: 'text-center',
          sortable: false,
          hidden: true,
        },
        {
          id: 28,
          label: 'Roll Off',
          field: 'roll_off_image',
          tdClass: 'text-center',
          sortable: false,
          hidden: true,
        },
        {
          id: 30,
          label: 'Action Count',
          field: 'action_count',
          type: 'number',
          tdClass: 'text-center',
          hidden: true,
        },
        {
          id: 31,
          label: 'Actual Action Count',
          field: 'actual_action_count',
          type: 'number',
          tdClass: 'text-center',
          hidden: true,
        },
        {
          id: 32,
          label: 'Marker Count',
          field: 'marker_count',
          type: 'number',
          tdClass: 'text-center',
          hidden: true,
        },
        {
          id: 33,
          label: 'Reaction Count',
          field: 'reaction_count',
          type: 'number',
          tdClass: 'text-center',
          hidden: true,
        },
        {
          id: 34,
          label: '',
          field: 'fill_anchor',
          filterable: false,
          sortable: false,
          tdClass: 'bb-is-fill-wide',
          thClass: 'bb-is-fill-wide',
          width: '1px',
          hidden: false,
        },
        {
          id: 90,
          label: 'Visibility',
          field: 'visibility',
          sortable: false,
          tdClass: 'text-center',
          hidden: true,
        },
        {
          id: 91,
          label: 'Fatigue',
          field: 'fatigue',
          tdClass: 'text-center',
          hidden: true,
        },
        {
          id: 92,
          label: 'Bleeding',
          field: 'bleeding',
          type: 'number',
          tdClass: 'text-center',
          hidden: true,
        },
        {
          id: 93,
          label: 'Surprised',
          field: 'surprised',
          type: 'boolean',
          tdClass: 'text-center',
          hidden: true,
        },
        {
          id: 100,
          label: 'Counters',
          field: 'counters',
          type: 'number',
          tdClass: 'text-center',
          hidden: true,
        },
        {
          id: 110,
          label: 'Id',
          field: 'id',
          type: 'number',
          tdClass: 'text-center',
          hidden: true,
        },
        {
          id: 111,
          label: 'Slug',
          field: 'slug',
          tdClass: 'text-center',
          hidden: true,
        },
        {
          id: 112,
          label: 'Dupe Rank',
          field: 'dupe_rank',
          tdClass: 'text-center',
          hidden: true,
        },
        {
          id: 113,
          label: 'Is Ranked',
          field: 'is_ranked',
          tdClass: 'text-center',
          hidden: true,
        },
        {
          id: 114,
          label: 'Headline Marker 1',
          field: 'headline_marker1',
          tdClass: 'text-center',
          hidden: true,
        },
        {
          id: 115,
          label: 'Headline Marker 2',
          field: 'headline_marker2',
          tdClass: 'text-center',
          hidden: true,
        },
        {
          id: 116,
          label: 'Headline Marker 3',
          field: 'headline_marker3',
          tdClass: 'text-center',
          hidden: true,
        },
        {
          id: 117,
          label: 'Headline Marker 4',
          field: 'headline_marker4',
          tdClass: 'text-center',
          hidden: true,
        },
        {
          id: 118,
          label: 'Extra Ind',
          field: 'extra',
          tdClass: 'text-center',
          hidden: true,
        },
        {
          id: 119,
          label: 'Second Weapon',
          field: 'second_weapon',
          tdClass: 'text-center',
          hidden: true,
        },
        {
          id: 120,
          label: 'Shield',
          field: 'shield',
          tdClass: 'text-center',
          hidden: true,
        },
        {
          id: 121,
          label: 'Ranged',
          field: 'ranged',
          tdClass: 'text-center',
          hidden: true,
        },
        {
          id: 122,
          label: 'Magic',
          field: 'magic',
          tdClass: 'text-center',
          hidden: true,
        },
        {
          id: 123,
          label: 'Special Action',
          field: 'special_action',
          tdClass: 'text-center',
          hidden: true,
        },
        {
          id: 124,
          label: 'Special Reaction',
          field: 'special_reaction',
          tdClass: 'text-center',
          hidden: true,
        },
        {
          id: 125,
          label: 'Dupe Processed',
          field: 'dupe_processed',
          tdClass: 'text-center',
          hidden: true,
        },
      ]
    },
    resetHeadings() {
      this.headings = this.createBaseHeadings()
      this.loadActionCols()
      this.loadReactionCols()
      this.showSummaryHeadings()
    },
    applyPlayerColumnOverrides() {
      const seqColumn = this.headings.find((column) => column.field === 'seq')

      if (seqColumn) {
        seqColumn.hidden = true
      }
    },
    isHiddenCombatantForPlayer(combatant) {
      return String(combatant?.visibility || '').toLowerCase() === 'hidden'
    },
    isConcealedCombatantForPlayer(combatant) {
      return String(combatant?.visibility || '').toLowerCase() === 'concealed'
    },
    getConcealedCombatantName(name = '') {
      const trimmedName = String(name || '').trim()

      if (!trimmedName) {
        return ''
      }

      return trimmedName.charAt(0).toUpperCase()
    },
    createPlayerVisibleCombatant(combatant) {
      if (!this.isConcealedCombatantForPlayer(combatant)) {
        return combatant
      }

      const maskedCombatant = {
        ...combatant,
        name: this.getConcealedCombatantName(combatant.name),
        player_concealed: true,
      }

      ;[
        'category',
        'type',
        'race_name',
        'role',
        'dex',
        'ca',
        'cr',
        'damage_mod',
        'sr',
        'sr_adj',
        'sr_roll',
        'roll_off',
        'rank',
        'sr_roll_image',
        'roll_off_image',
      ].forEach((field) => {
        maskedCombatant[field] = ''
      })

      return maskedCombatant
    },
    applyPlayerVisibilityRules(combatants = this.combatants) {
      return combatants
        .filter((combatant) => !this.isHiddenCombatantForPlayer(combatant))
        .map((combatant) => this.createPlayerVisibleCombatant(combatant))
    },
    resetPlayerCombatPhase() {
      this.playerCombatPhase = 'standard'
      this.arena_extra_ready = false
      this.arena_extra_action = 0
      this.arena_bleeders_ready = false
    },
    shouldUseCombatTextAnimations() {
      const config = this.getConfig || {}

      return config.animation === true || config.animations === true
    },
    showCombatTextOverlay(message = '') {
      if (!message || !this.shouldUseCombatTextAnimations()) {
        return
      }

      if (this.combatTextOverlayTimer) {
        clearTimeout(this.combatTextOverlayTimer)
        this.combatTextOverlayTimer = null
      }

      this.combatTextOverlayVisible = false
      this.combatTextOverlayMessage = message
      this.combatTextOverlayKey++

      window.requestAnimationFrame(() => {
        this.combatTextOverlayVisible = true
      })

      this.combatTextOverlayTimer = window.setTimeout(() => {
        this.combatTextOverlayVisible = false
        this.combatTextOverlayTimer = null
      }, 1500)
    },
    loadActionCols() {
      const caExtra = this.maxCA + 1
      this.new_col = this.headings.findIndex(
        (column) => column.field == 'fill_anchor',
      )

      const fillerBeforeLastAction = {
        id: this.new_col,
        label: '',
        field: 'fill_last_action_before',
        filterable: false,
        sortable: false,
        width: '1px',
        tdClass: 'bb-is-fill-wide',
        thClass: 'bb-is-fill-wide',
        hidden: true,
      }
      this.headings.splice(this.new_col, 0, fillerBeforeLastAction)
      this.new_col++

      const lastAction = {
        id: this.new_col,
        label: 'Last Action',
        field: 'prev_round_action_image',
        tdClass: 'text-center bb-combat-image-cell',
        filterable: false,
        sortable: false,
        width: '90px',
        hidden: true,
      }
      this.headings.splice(this.new_col, 0, lastAction)
      this.new_col++

      const fillerAfterLastAction = {
        id: this.new_col,
        label: '',
        field: 'fill_last_action_after',
        filterable: false,
        sortable: false,
        width: '1px',
        tdClass: 'bb-is-fill-wide',
        thClass: 'bb-is-fill-wide',
        hidden: true,
      }
      this.headings.splice(this.new_col, 0, fillerAfterLastAction)
      this.new_col++

      for (let actionNumber = 1; actionNumber <= caExtra; actionNumber++) {
        if (actionNumber === caExtra) {
          const extraAttackOptions = {
            id: this.new_col,
            label: 'Off-Hand',
            field: 'extra_attack_options',
            tdClass: 'text-center bb-combat-image-cell',
            filterable: false,
            sortable: false,
            width: '120px',
            hidden: true,
          }
          this.headings.splice(this.new_col, 0, extraAttackOptions)
          this.new_col++

          const lastActionImage = {
            id: this.new_col,
            label: 'Last Action',
            field: 'last_action_image',
            tdClass: 'text-center bb-combat-image-cell',
            filterable: false,
            sortable: false,
            width: '90px',
            hidden: true,
          }
          this.headings.splice(this.new_col, 0, lastActionImage)
          this.new_col++

          const nextActionImage = {
            id: this.new_col,
            label: 'Next Action',
            field: 'next_action_image',
            tdClass: 'text-center bb-combat-image-cell',
            filterable: false,
            sortable: false,
            width: '90px',
            hidden: true,
          }
          this.headings.splice(this.new_col, 0, nextActionImage)
          this.new_col++

          const noAttackReason = {
            id: this.new_col,
            label: 'No Extra Attack Reason',
            field: 'no_attack_reason',
            tdClass: 'text-center',
            filterable: false,
            sortable: false,
            width: '240px',
            hidden: true,
          }
          this.headings.splice(this.new_col, 0, noAttackReason)
          this.new_col++

          const fillerBeforeExtra = {
            id: this.new_col,
            label: '',
            field: 'fill_extra_before',
            filterable: false,
            sortable: false,
            width: '1px',
            tdClass: 'bb-is-fill-wide',
            thClass: 'bb-is-fill-wide',
            hidden: true,
          }
          this.headings.splice(this.new_col, 0, fillerBeforeExtra)
          this.new_col++
        }

        const actionColumn = {
          id: this.new_col,
          label: actionNumber === caExtra ? 'Extra' : 'CA' + actionNumber,
          field: 'ca' + actionNumber,
          tdClass: 'text-center bb-combat-image-cell',
          filterable: false,
          sortable: false,
          width: '90px',
          hidden: false,
        }
        this.headings.splice(this.new_col, 0, actionColumn)
        this.new_col++

        const actionNameColumn = {
          id: this.new_col,
          label: 'CA Name' + actionNumber,
          field: 'ca_name' + actionNumber,
          tdClass: 'text-center',
          filterable: false,
          sortable: false,
          hidden: true,
        }
        this.headings.splice(this.new_col, 0, actionNameColumn)
        this.new_col++

        const actionSlugColumn = {
          id: this.new_col,
          label: 'CA Slug' + actionNumber,
          field: 'ca_slug' + actionNumber,
          tdClass: 'text-center',
          filterable: false,
          sortable: false,
          hidden: true,
        }
        this.headings.splice(this.new_col, 0, actionSlugColumn)
        this.new_col++

        const actionTypeColumn = {
          id: this.new_col,
          label: 'CA Type' + actionNumber,
          field: 'ca_type' + actionNumber,
          tdClass: 'text-center',
          filterable: false,
          sortable: false,
          hidden: true,
        }
        this.headings.splice(this.new_col, 0, actionTypeColumn)
        this.new_col++
      }

      const filler = {
        id: this.new_col,
        label: '',
        field: 'fill2',
        filterable: false,
        sortable: false,
        width: '1px',
        tdClass: 'bb-is-fill-wide',
        thClass: 'bb-is-fill-wide',
      }
      this.headings.splice(this.new_col, 0, filler)
    },
    loadReactionCols() {
      this.new_col = this.headings.length

      for (
        let reactionNumber = 1;
        reactionNumber <= this.maxCR;
        reactionNumber++
      ) {
        const reactionColumn = {
          id: this.new_col,
          label: 'CR' + reactionNumber,
          field: 'cr' + reactionNumber,
          tdClass: 'text-center bb-combat-image-cell',
          filterable: false,
          sortable: false,
          width: '90px',
        }
        this.headings.splice(this.new_col, 0, reactionColumn)
        this.new_col++

        const reactionNameColumn = {
          id: this.new_col,
          label: 'CR Name' + reactionNumber,
          field: 'cr_name' + reactionNumber,
          tdClass: 'text-center',
          filterable: false,
          sortable: false,
          hidden: true,
        }
        this.headings.splice(this.new_col, 0, reactionNameColumn)
        this.new_col++

        const reactionSlugColumn = {
          id: this.new_col,
          label: 'CR Slug' + reactionNumber,
          field: 'cr_slug' + reactionNumber,
          tdClass: 'text-center',
          filterable: false,
          sortable: false,
          hidden: true,
        }
        this.headings.splice(this.new_col, 0, reactionSlugColumn)
        this.new_col++
      }

      const filler = {
        id: this.new_col,
        label: '',
        field: 'fill3',
        filterable: false,
        sortable: false,
        width: '1px',
        tdClass: 'bb-is-fill',
      }
      this.headings.splice(this.new_col, 0, filler)
    },
    showDetailHeadings() {
      const colNumbers = getDescDetailCols({
        headings: this.headings,
        filter_status: this.filterStatus,
        view_round: this.view_round,
        cur_round: this.arena_round,
      })

      this.headings.map((column) => (column.hidden = true))
      this.toggleDesc = false

      colNumbers.forEach((colNumber) => {
        this.headings[colNumber].hidden = false
      })

      this.applyPlayerColumnOverrides()
      this.headings = [...this.headings]
      this.emitter.emit('update-scoreboard')
    },
    showSummaryHeadings() {
      const colNumbers = getDescSummaryCols({
        headings: this.headings,
        filter_status: this.filterStatus,
        view_round: this.view_round,
        cur_round: this.arena_round,
      })

      this.headings.map((column) => (column.hidden = true))
      this.toggleDesc = true

      colNumbers.forEach((colNumber) => {
        this.headings[colNumber].hidden = false
      })

      this.applyPlayerColumnOverrides()
      this.headings = [...this.headings]
      this.emitter.emit('update-scoreboard')
    },
    async showExtraCols(combatants = this.combatants) {
      const extraParm = {
        arena_slug: this.arenaSlug,
        round: this.arena_round,
      }
      const extras = await getExtraAttacks(extraParm)

      const nextCombatants = setLocalCombatantsExtras(combatants, extras)
      this.arena_extra_ready = false
      this.arena_extra_action = this.maxCA + 1
      this.arena_bleeders_ready = false

      nextCombatants.forEach((combatant) => {
        combatant.last_action_image = getCombatantLastRoundActionImage(
          combatant,
          this.maxCA,
        )
        combatant.next_action_image = ''
        combatant.no_attack_reason = ''
      })

      this.headings.map((column) => (column.hidden = true))
      loadCombatantExtraImages(nextCombatants)

      const colNumbers = getToggleExtraCols(this.headings)
      colNumbers.forEach((colNumber) => {
        if (colNumber >= 0) {
          this.headings[colNumber].hidden = false
        }
      })

      await Promise.all(
        nextCombatants.map(async (combatant) => {
          const nextAction = await getCombatantNextRoundAction(
            combatant,
            this.arena_round,
          )

          let onCounters = false

          if (nextAction) {
            combatant.next_action_image = nextAction?.action_image || ''
          } else {
            const nextMarker = await getCombatantNextRoundMarker(
              combatant,
              this.arena_round,
            )

            if (nextMarker) {
              combatant.next_action_image = nextMarker.marker_image || ''
              if (nextMarker.marker_name == 'counter') {
                onCounters = true
              }
            }
          }

          combatant.no_attack_reason = getCombatantExtraAttackInvalidReason({
            combatant,
            maxCA: this.maxCA,
            maxCR: this.maxCR,
            nextAction,
            onCounters,
          })

          if (combatant.no_attack_reason !== '' && combatant.extra == true) {
            const idx = nextCombatants.findIndex((c) => c.id === combatant.id)

            if (idx !== -1) {
              nextCombatants[idx].extra = false
              loadSpecificCombatantExtraImage(idx, false, nextCombatants)
            }
          } else if (
            combatant.no_attack_reason === '' &&
            combatant.extra == false
          ) {
            const idx = nextCombatants.findIndex((c) => c.id === combatant.id)

            if (idx !== -1) {
              nextCombatants[idx].extra = true
              loadSpecificCombatantExtraImage(idx, false, nextCombatants)
            }
          }
        }),
      )

      this.applyPlayerColumnOverrides()
      this.headings = [...this.headings]
      this.emitter.emit('update-scoreboard')
      return nextCombatants
    },
    showBleederCombatants(combatants = this.combatants) {
      const nextCombatants = getBleeders(
        this.filterCombatantStatuses('All', combatants),
      )
      this.arena_extra_ready = false
      this.arena_extra_action = 0
      this.arena_bleeders_ready = false
      this.showSummaryHeadings()
      return nextCombatants
    },
    async applyPlayerCombatPhase(combatants = this.combatants) {
      if (this.arena_locked || this.isArenaInactive) {
        return combatants
      }

      if (this.view_round !== this.arena_round) {
        if (this.toggleDesc) {
          this.showSummaryHeadings()
        } else {
          this.showDetailHeadings()
        }
        return combatants
      }

      if (this.playerCombatPhase === 'extra_attacks') {
        return await this.showExtraCols(combatants)
      }

      if (this.playerCombatPhase === 'bleeders') {
        return this.showBleederCombatants(combatants)
      }

      this.resetPlayerCombatPhase()

      if (this.toggleDesc) {
        this.showSummaryHeadings()
      } else {
        this.showDetailHeadings()
      }

      return combatants
    },
    handlePlayerViewTransition(message = {}) {
      const nextPhase = message?.phase || 'standard'

      if (nextPhase === 'combat_started') {
        this.resetPlayerCombatPhase()
        this.view_round = Number(message?.round || 0)
        this.setViewRound(this.view_round)
        this.showCombatTextOverlay('Combat Started')
        return
      }

      if (nextPhase === 'view_round') {
        this.view_round = Number(message?.viewRound || this.arena_round || 0)
        this.setViewRound(this.view_round)
        return
      }

      if (nextPhase === 'extra_attacks') {
        this.playerCombatPhase = 'extra_attacks'
        this.arena_extra_action = this.maxCA + 1
        this.arena_extra_ready = false
        this.arena_bleeders_ready = false
        return
      }

      if (nextPhase === 'bleeders') {
        this.playerCombatPhase = 'bleeders'
        this.arena_extra_action = 0
        this.arena_extra_ready = false
        this.arena_bleeders_ready = false
        return
      }

      if (nextPhase === 'new_round') {
        this.resetPlayerCombatPhase()
        this.showCombatTextOverlay('New Round')
        return
      }

      this.resetPlayerCombatPhase()
    },
    filterCombatantStatuses(filterStatusDesc, combatants = this.combatants) {
      if (filterStatusDesc == 'All') {
        return [...combatants]
      }

      return combatants.filter(
        (combatant) => combatant.status == filterStatusDesc,
      )
    },
    async loadPreviousRoundActionImages(
      round = this.view_round,
      combatants = this.combatants,
    ) {
      if (round <= 1) {
        combatants.forEach((combatant) => {
          combatant.prev_round_action_image = ''
        })
        return combatants
      }

      await Promise.all(
        combatants.map(async (combatant) => {
          combatant.prev_round_action_image =
            await getCombatantPreviousRoundActionImage(combatant, round)
        }),
      )

      return combatants
    },
    refreshArenaStateFromStore() {
      const arena = this.getCampaign

      if (!arena) {
        return false
      }

      if (this.arena_round && arena.round !== this.arena_round) {
        this.resetPlayerCombatPhase()
        this.view_round = arena.round
      }

      this.arena_status = arena.status
      this.arena_round = arena.round
      this.arena_action = arena.action
      this.arena_locked = arena.locked
      this.arena_rolled = arena.rolled
      this.arena_in_progress = arena.in_progress
      this.arena_round_completed = arena.round_completed
      if (!this.view_round) {
        this.view_round = arena.round
      }
      this.setViewRound(this.view_round)
      this.setArenaSubStatus(this.arenaSubStatus)
      return true
    },
    async rebuildCombatTable(
      round = this.view_round,
      { preserveExistingTable = true } = {},
    ) {
      if (!(preserveExistingTable && this.loadReady)) {
        this.loadReady = false
      }

      const combatants = await loadCampaignRoundSlots(round)
      loadLocalCombatantProps(combatants)
      loadCombatantImages()
      loadCombatantHeadlineMarkers()

      let nextCombatants = refreshCombatantOrder(
        combatants.map((combatant) => ({ ...combatant })),
      )

      this.filterStatus = this.getActiveCombatantCount > 0
      nextCombatants = this.filterCombatantStatuses(
        getFilterStatusDesc(this.filterStatus),
        nextCombatants,
      )
      nextCombatants = await this.loadPreviousRoundActionImages(
        round,
        nextCombatants,
      )

      nextCombatants = await this.applyPlayerCombatPhase(nextCombatants)
      nextCombatants = this.applyPlayerVisibilityRules(nextCombatants)

      this.combatants = nextCombatants
      this.loadReady = true
      this.emitter.emit('update-scoreboard')
    },
    async ensureArenaSocketConnection() {
      const arenaSlug = this.arenaSlug

      if (!arenaSlug) {
        return
      }

      const existingConnection = this.arenaSocketConnection
      if (
        existingConnection &&
        existingConnection.bbArenaSlug === arenaSlug &&
        (existingConnection.readyState === WebSocket.OPEN ||
          existingConnection.readyState === WebSocket.CONNECTING)
      ) {
        return
      }

      if (
        existingConnection &&
        existingConnection.readyState === WebSocket.OPEN
      ) {
        existingConnection.close(1000)
      }

      const connection = createSocketConnection('arena', arenaSlug)
      connection.bbArenaSlug = arenaSlug
      connection.onmessage = async (event) => {
        const data = JSON.parse(event.data)
        const message = data.message

        if (message?.type === 'hidden_roll_delivery') {
          return
        }

        if (message?.type === 'player_view_transition') {
          this.handlePlayerViewTransition(message)
        }

        await this.reloadArenaState()
      }

      this.arenaSocketConnection = connection
    },
    async reloadArenaState() {
      if (!this.arenaSlug) {
        return
      }

      if (this.isReloadingArenaState) {
        this.hasPendingArenaReload = true
        return
      }

      this.isReloadingArenaState = true

      try {
        do {
          this.hasPendingArenaReload = false

          await this.loadCampaign(this.arenaSlug)
          this.refreshArenaStateFromStore()
          this.emitter.emit('update-scoreboard')

          if (!this.arena_locked && !this.isArenaInactive) {
            const targetRound = this.view_round || this.arena_round
            await this.rebuildCombatTable(targetRound)
          } else {
            this.loadReady = false
          }
        } while (this.hasPendingArenaReload)
      } finally {
        this.isReloadingArenaState = false
      }
    },
    async loadPlayerCombat(arenaSlug) {
      this.isLoading = true
      this.loadReady = false
      this.errorMessage = ''
      this.resetPlayerCombatPhase()

      try {
        await Promise.all([
          this.loadConfig(),
          this.loadStaticStatuses(),
          this.loadStaticMarkers(),
          this.loadStaticOptions(),
          this.loadStaticVisibilities(),
          this.loadStaticFatigues(),
          this.loadStaticActions(),
          this.loadStaticReactions(),
          this.loadStaticRoles(),
          this.loadCampaign(arenaSlug),
        ])

        this.SetView('player_combat')
        this.maxCA = this.getConfig?.max_ca || 0
        this.maxCR = this.getConfig?.max_cr || 0

        if (!this.refreshArenaStateFromStore()) {
          throw new Error('Campaign failed to load')
        }

        this.resetHeadings()
        await this.ensureArenaSocketConnection()
        this.emitter.emit('update-scoreboard')

        if (!this.arena_locked && !this.isArenaInactive) {
          await this.rebuildCombatTable(this.arena_round, {
            preserveExistingTable: false,
          })
        } else {
          this.loadReady = false
        }
      } catch (error) {
        this.errorMessage =
          error?.response?.data?.detail ||
          error?.message ||
          'Unable to load player combat view.'
      } finally {
        this.isLoading = false
      }
    },
    handleToggleDescriptionHeadings() {
      if (this.toggleDesc) {
        this.showDetailHeadings()
      } else {
        this.showSummaryHeadings()
      }
    },
    handleNextViewRound() {
      this.view_round = changeViewRound('next')
      void this.rebuildCombatTable(this.view_round)
    },
    handlePreviousViewRound() {
      this.view_round = changeViewRound('previous')
      void this.rebuildCombatTable(this.view_round)
    },
  },
  mounted() {
    this.SetView('player_combat')
    this.emitter.on(
      'toggle-description-headings',
      this.handleToggleDescriptionHeadings,
    )
    this.emitter.on('next-view-round', this.handleNextViewRound)
    this.emitter.on('previous-view-round', this.handlePreviousViewRound)
  },
  beforeUnmount() {
    if (this.arenaSocketConnection?.readyState === WebSocket.OPEN) {
      this.arenaSocketConnection.close(1000)
    }

    if (this.combatTextOverlayTimer) {
      clearTimeout(this.combatTextOverlayTimer)
      this.combatTextOverlayTimer = null
    }

    this.emitter.off(
      'toggle-description-headings',
      this.handleToggleDescriptionHeadings,
    )
    this.emitter.off('next-view-round', this.handleNextViewRound)
    this.emitter.off('previous-view-round', this.handlePreviousViewRound)
    this.arenaSocketConnection = null
  },
}
</script>

<style scoped>
.bb-player-combat-view {
  min-height: 100vh;
}

.bb-player-loader {
  margin-top: 3rem;
}

.bb-player-combat-content {
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  min-height: calc(100vh - 6.5rem);
}

.bb-player-locked-state {
  align-items: center;
  display: flex;
  flex-direction: column;
  justify-content: center;
  min-height: 60vh;
  padding: 2rem 1rem;
}

.bb-player-lock-image {
  width: min(7rem, 15vw);
  height: auto;
}

.bb-player-locked-message {
  font-size: 1.8rem;
  max-width: 42rem;
  text-align: center;
}

.bb-player-waiting-state {
  align-items: center;
  display: flex;
  flex-direction: column;
  justify-content: center;
  min-height: 60vh;
  padding: 2rem 1rem;
}

.bb-player-waiting-message {
  font-size: 1.7rem;
  max-width: 28rem;
  text-align: center;
}

.bb-player-combat-row {
  align-items: stretch;
  flex: 1 1 auto;
  min-height: 0;
}

.bb-player-table-column {
  display: flex;
  min-height: 0;
}

.bb-player-table-surface {
  height: 100%;
  min-height: 0;
  position: relative;
  width: 100%;
}

.bb-player-table-surface :deep(.bb-combatants-table-wrapper) {
  height: 100%;
  min-height: calc(100vh - 11.5rem);
}

.bb-egg-timer {
  animation: bb-egg-timer-flip 3.2s ease-in-out infinite;
  border: 0.45rem solid #d6d7db;
  border-radius: 1.9rem;
  box-sizing: border-box;
  height: 7.2rem;
  overflow: hidden;
  position: relative;
  width: 4.7rem;
}

.bb-egg-timer::before,
.bb-egg-timer::after {
  background: #d6d7db;
  content: '';
  height: 0.34rem;
  left: 0.4rem;
  position: absolute;
  width: calc(100% - 0.8rem);
}

.bb-egg-timer::before {
  top: 0.6rem;
}

.bb-egg-timer::after {
  bottom: 0.6rem;
}

.bb-egg-timer-sand {
  background: linear-gradient(180deg, #e6b84f 0%, #e29a27 100%);
  left: 50%;
  position: absolute;
  transform: translateX(-50%);
}

.bb-egg-timer-sand-top {
  animation: bb-egg-timer-top 3.2s linear infinite;
  border-radius: 0.35rem 0.35rem 0.95rem 0.95rem;
  top: 1.05rem;
  width: 2.05rem;
}

.bb-egg-timer-sand-bottom {
  animation: bb-egg-timer-bottom 3.2s linear infinite;
  border-radius: 0.95rem 0.95rem 0.35rem 0.35rem;
  bottom: 1.05rem;
  width: 2.05rem;
}

@keyframes bb-egg-timer-flip {
  0%,
  40% {
    transform: rotate(0deg);
  }

  50%,
  90% {
    transform: rotate(180deg);
  }

  100% {
    transform: rotate(360deg);
  }
}

@keyframes bb-egg-timer-top {
  0% {
    height: 2.35rem;
  }

  45% {
    height: 0.48rem;
  }

  50% {
    height: 0.28rem;
  }

  95%,
  100% {
    height: 2.35rem;
  }
}

@keyframes bb-egg-timer-bottom {
  0% {
    height: 0.48rem;
  }

  45% {
    height: 2.35rem;
  }

  50% {
    height: 2.5rem;
  }

  95%,
  100% {
    height: 0.48rem;
  }
}
</style>
